import React, { useEffect, useState } from 'react';

import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';
import { useIsMobileDevice } from '../../helpers/hooks/useIsMobileDevice';
import { UNIVERSAL_ENV_VARS } from '../../constants/environmentVariables';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { getZendeskToken } from '../../api/rest/getZendeskToken';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../helpers/logging';
import { reportMykissClientError } from '../../helpers/errorHandling/client';

const ZENDESK_ZINDEX = 20;
const STICKY_BOTTOM_OFFSET = 88;

export const ZendeskSupportChat = ({
  hasStickyBottomBanner = false,
  disableSupportChat = false,
}: {
  hasStickyBottomBanner?: boolean;
  disableSupportChat?: boolean;
}) => {
  const isMobileDevice = useIsMobileDevice();
  const text_trench = 'rgb(6, 29, 51)';
  const { currentPage } = useAnalytics();
  const [loadScript, setLoadScript] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isZendeskLoaded, setIsZendeskLoaded] = useState(false);

  const { getToken, isLoggedIn, isLoadingUser } = useAuth();

  useEffect(() => {
    if (!isZendeskLoaded || !isLoggedIn || !window.zE) {
      return;
    }

    window.zE('messenger', 'loginUser', function (callback: (token: string) => void) {
      getZendeskToken(getToken)
        .then(token => {
          callback(token);
        })
        .catch(error => {
          logger.error('Zendesk: Failed to fetch new JWT:', error);
          reportMykissClientError(error, 'Zendesk: Failed to fetch new JWT');
        });
    });
  }, [isZendeskLoaded, getToken, isLoggedIn]);

  const track = (): void => {
    void trackEvent({
      name: 'mykiss_support_bubble_open',
      properties: { source: currentPage },
    });
  };

  const setupZendeskListeners = () => {
    if (window.zE) {
      window.zE('messenger:on', 'close', () => {
        setIsChatOpen(false);
      });
      window.zE('messenger:on', 'open', () => {
        setIsChatOpen(true);
        setLoadScript(false);
      });
    }
  };

  const isLoadingZendeskChat = (loadScript && !isChatOpen) || isLoadingUser;

  return disableSupportChat ? (
    <></>
  ) : (
    <>
      {isLoadingZendeskChat && (
        <span
          className="fixed right-[18px] z-20 box-border size-[45px] cursor-pointer rounded-full px-[10px] pt-[12px] pb-[8px] -outline-offset-4"
          id="zendeskLoader"
          style={{
            outline: 'rgba(0, 103, 244, 0.4) solid 0px',
            background: text_trench,
            zIndex: ZENDESK_ZINDEX + 1,
            bottom: isMobileDevice && hasStickyBottomBanner ? `${STICKY_BOTTOM_OFFSET}px` : '18px',
          }}
        >
          <LoadingSpinner className="-ml-1 text-white" isMini />
        </span>
      )}
      <button
        type="button"
        id="zendeskLauncher"
        aria-label="Open Chat"
        title="Support Chat"
        onClick={() => {
          track();
          if (window.zE) {
            window.zE('messenger', 'open');
          } else {
            // Load script dynamically
            setLoadScript(true);
            const script = document.createElement('script');
            script.id = 'ze-snippet';
            script.src = `https://static.zdassets.com/ekr/snippet.js?key=${UNIVERSAL_ENV_VARS.ZENDESK_KEY}`;
            script.onload = () => {
              setIsZendeskLoaded(true);
              setupZendeskListeners();
              // Open chat once script is loaded
              if (window.zE) {
                window.zE('messenger', 'open');
              }
            };
            document.body.appendChild(script);
          }
        }}
        className="fixed right-[18px] z-20 box-border size-[45px] cursor-pointer rounded-full px-[10px] pt-[12px] pb-[8px] -outline-offset-4"
        style={{
          outline: 'rgba(0, 103, 244, 0.4) solid 0px',
          background: text_trench,
          zIndex: ZENDESK_ZINDEX,
          bottom: isMobileDevice && hasStickyBottomBanner ? `${STICKY_BOTTOM_OFFSET}px` : '18px',
        }}
      >
        <span className="bg-error absolute top-0 right-[4px] left-auto hidden size-5 rounded-full text-center text-xs leading-5 text-white" />
        <span>
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 560 560">
            <g fill="#FFFFFF" transform="translate(60 77)">
              <path d="M363.373365,0 L10.16125,0 C5.42701923,0 0,3.96105769 0,8.62971154 L0,235.205385 C0,239.872981 5.42701923,244.326923 10.16125,244.326923 L70.8653846,244.326923 L70.8653846,338.349423 L166.193077,244.326923 L363.373365,244.326923 C368.108654,244.326923 371.25,239.872981 371.25,235.205385 L371.25,8.62971154 C371.25,3.96105769 368.107596, 0 363.373365,0 L363.373365,0 Z" />
              <path d="M431.957308,66.6346154 L397.692308,66.6346154 L397.692308,242.814423 C397.692308,259.72375 390.378365,270.769231 371.089231,270.769231 L179.909231,270.769231 L138.759712,312.019231 L275.929712,312.019231 L371.25,406.153846 L371.25,312.019231 L431.957308,312.019231 C436.693654,312.019231 440,307.511346 440,302.836346 L440,76.2627885 C440,71.5941346 436.693654,66.6346154 431.957308,66.6346154 L431.957308,66.6346154 Z" />
            </g>
          </svg>
        </span>
      </button>
    </>
  );
};
